/**
 * PRODUCTION API CLIENT
 * =====================
 *
 * Central axios instance with:
 * - Auto token injection
 * - Auth error handling (redirect to login)
 * - Session extension on activity
 * - Token expiry detection
 */

import axios from "axios";
import Cookies from "js-cookie";

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  // Your existing environment variables
  baseURL: process.env.REACT_APP_BACKEND_NETWORK_CALL_V || "",
  authURL: process.env.REACT_APP_BACKEND_NETWORK_CALL_AUTH_ENDPOINT || "",
  timeout: 30000,
  isDev: process.env.REACT_APP_MODE === "development",

  // Session settings (60-minute session)
  sessionWarningTime: 5 * 60 * 1000, // Show warning 5 min before expiry
  activityDebounce: 60 * 1000, // Debounce activity checks (1 min)

  // Cold-start retry: Railway containers can drop the first TCP connection
  // after waking from sleep.  Retry network-level failures (no HTTP response)
  // up to this many times before surfacing the error to the user.
  networkRetryAttempts: 2,
  networkRetryDelay: 1000, // ms between retries (doubles each attempt)
};

// ============================================
// TOKEN HELPERS
// ============================================
export const getToken = () =>
  CONFIG.isDev ? localStorage.getItem("user_tk") : Cookies.get("connectedMe");

export const setToken = (token) => {
  if (CONFIG.isDev) {
    localStorage.setItem("user_tk", token);
  } else {
    Cookies.set("connectedMe", token, {
      secure: true,
      sameSite: "strict",
      expires: 7,
    });
  }
};

export const getLoginType = () => localStorage.getItem("loginType");

export const clearAuth = () => {
  localStorage.removeItem("user_tk");
  localStorage.removeItem("loginType");
  localStorage.removeItem("lastActivityTime");
  localStorage.removeItem("sessionWarningShown");
  Cookies.remove("connectedMe");
  Cookies.remove("loginType");
};

// ============================================
// JWT HELPERS
// ============================================
export const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch (err) {
    console.error("[API] Failed to decode JWT:", err);
    return null;
  }
};

export const getTokenExpiry = (token) => {
  const payload = decodeJwt(token);
  if (payload?.exp) {
    return new Date(payload.exp * 1000);
  }
  return null;
};

export const isTokenExpired = (token) => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  return Date.now() >= expiry.getTime();
};

export const getTimeUntilExpiry = (token) => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  return Math.max(0, expiry.getTime() - Date.now());
};

// ============================================
// AUTH ERROR DETECTION
// ============================================
const AUTH_ERROR_CODES = [401, 403];
const AUTH_ERROR_KEYWORDS = [
  "unauthorized",
  "unauthenticated",
  "token expired",
  "invalid token",
  "jwt expired",
  "session expired",
  "session has expired",
  "authentication failed",
  "access denied",
  "not authenticated",
  "could not validate",
];

const isAuthError = (error) => {
  if (!error.response) return false;

  const { status, data } = error.response;

  // Check status code
  if (AUTH_ERROR_CODES.includes(status)) return true;

  // Check error message
  const message = (
    data?.detail ||
    data?.message ||
    data?.error ||
    ""
  ).toLowerCase();
  return AUTH_ERROR_KEYWORDS.some((keyword) => message.includes(keyword));
};

// ============================================
// SESSION EXTENSION
// ============================================
let isExtending = false;
let lastExtendTime = 0;

const extendSession = async () => {
  const token = getToken();
  if (!token || isExtending) return false;

  // Debounce - don't extend more than once per minute
  const now = Date.now();
  if (now - lastExtendTime < 60000) return false;

  isExtending = true;
  lastExtendTime = now;

  try {
    // Use auth endpoint for session extension
    const response = await axios.post(
      `${CONFIG.authURL}extend_session`,
      { session_token: token },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000,
      }
    );

    // Backend response shape: { status, message, data: { access_token, token_type } }
    const newToken = response.data?.data?.access_token;
    if (newToken) {
      setToken(newToken);
      console.log("[Session] Extended successfully");
      return true;
    }
    return false;
  } catch (err) {
    console.error("[Session] Extension failed:", err);
    return false;
  } finally {
    isExtending = false;
  }
};

// ============================================
// REDIRECT HANDLER
// ============================================
let isRedirecting = false;

const handleAuthFailure = (reason = "auth_error") => {
  if (isRedirecting) return;
  isRedirecting = true;

  console.warn(`[API] Auth failure (${reason}), redirecting to login...`);
  clearAuth();

  // Hard redirect to clear all state
  window.location.href = "/";
};

// ============================================
// CREATE API INSTANCE (for regular API calls)
// ============================================
const api = axios.create({
  baseURL: CONFIG.baseURL,
  timeout: CONFIG.timeout,
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

// ============================================
// CREATE AUTH API INSTANCE (for auth-specific calls)
// ============================================
const authApi = axios.create({
  baseURL: CONFIG.authURL,
  timeout: CONFIG.timeout,
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================
api.interceptors.request.use(
  async (config) => {
    const token = getToken();

    if (token) {
      // Check if token is expired BEFORE making request
      if (isTokenExpired(token)) {
        handleAuthFailure("token_expired_before_request");
        return Promise.reject(new Error("Token expired"));
      }

      // Check if token is about to expire
      const timeLeft = getTimeUntilExpiry(token);
      if (timeLeft > 0 && timeLeft < CONFIG.sessionWarningTime) {
        // Try to extend session in background
        extendSession();
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    if (CONFIG.isDev) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const config = error.config || {};

    // ------------------------------------------------------------------
    // Cold-start / network-level retry
    // When Railway wakes from sleep the first TCP connection attempt can
    // fail before any HTTP response is received (error.response is null).
    // Retry these transparently so the user never sees a network error on
    // the first request after an inactivity sleep cycle.
    // ------------------------------------------------------------------
    if (!error.response) {
      const attempt = config.__networkRetryCount || 0;
      if (attempt < CONFIG.networkRetryAttempts) {
        config.__networkRetryCount = attempt + 1;
        const delay = CONFIG.networkRetryDelay * Math.pow(2, attempt); // exponential back-off
        if (CONFIG.isDev) {
          console.warn(`[API] Network error — retrying (${config.__networkRetryCount}/${CONFIG.networkRetryAttempts}) in ${delay}ms`);
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }
      console.error("[API] Network error after retries:", error.message);
      return Promise.reject(error);
    }

    const { status } = error.response;

    if (isAuthError(error)) {
      if (status === 401 && !config.__isRetryRequest) {
        config.__isRetryRequest = true;

        try {
          const extended = await extendSession();
          if (extended) {
            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${getToken()}`
            };
            return api(config);
          }
        } catch {
          handleAuthFailure("refresh_failed");
        }
      }

      handleAuthFailure("api_auth_error");
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH API INTERCEPTORS (same logic)
// ============================================
authApi.interceptors.request.use(
  async (config) => {
    const token = getToken();

    if (token) {
      if (isTokenExpired(token)) {
        handleAuthFailure("token_expired_before_request");
        return Promise.reject(new Error("Token expired"));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (CONFIG.isDev) {
      console.log(`[AuthAPI] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};

    // Cold-start retry for auth API (same logic as main api instance)
    if (!error.response) {
      const attempt = config.__networkRetryCount || 0;
      if (attempt < CONFIG.networkRetryAttempts) {
        config.__networkRetryCount = attempt + 1;
        const delay = CONFIG.networkRetryDelay * Math.pow(2, attempt);
        if (CONFIG.isDev) {
          console.warn(`[AuthAPI] Network error — retrying (${config.__networkRetryCount}/${CONFIG.networkRetryAttempts}) in ${delay}ms`);
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        return authApi(config);
      }
      console.error("[AuthAPI] Network error after retries:", error.message);
      return Promise.reject(error);
    }

    const { status } = error.response;

    if (CONFIG.isDev) {
      console.error(`[AuthAPI] Error ${status}:`, error.response.data?.detail);
    }

    if (isAuthError(error)) {
      const hasToken = getToken();

      // Only clear session if user was logged in.
      // Reject normally so callers' .catch() and finally blocks always run.
      if (hasToken) {
        handleAuthFailure("auth_api_error");
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// EXPORT
// ============================================
export { authApi }; // For auth-specific calls (login, extend_session, etc.)
export default api; // For regular API calls
